import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { BASE_URL, GLOBAL_PREFIX } from '../../../styles/constants';
import {
  CartItemType,
  OrderError,
  useCartContext,
} from '../../context/useCartContext';
import useJwt from '../../utils/useJwt';
import useLocaleQueryFilter from '../../utils/useLocaleQueryFilter';
import { AddressType } from '../queries/useAccountAddress';

type CreateOrderItemType = Pick<CartItemType, 'quantity' | 'variantId'>;
enum CreateOrderResponseStatus {
  'SUCCESS' = 'SUCCESS',
  'PARTIAL' = 'PARTIAL',
}

export type CreateOrderInput = {
  totalMilesAmount: number;
  address?: AddressType;
  gift?: number;
  giftRecipient?: string;
  giftMessage?: string;
  items: CreateOrderItemType[];
};

type CreateOrderResponse = {
  orderId: number;
  status: CreateOrderResponseStatus;
  errors: OrderError[];
};

function useCreateOrder() {
  const queryClient = useQueryClient();
  const jwt = useJwt();
  const { licenseId } = useLocaleQueryFilter();
  const { setOrderErrors, emptyCart, partiallyEmptyCart } = useCartContext();

  const hardCodedSessionId = '5uq3jv8rkedviqk38q4uhggb8r';
  const sessionId = window.config?.SESSION_ID ?? hardCodedSessionId;
  if (sessionId === hardCodedSessionId)
    console.log(
      '[DEBUG WARNING] This is a hard coded sessionID',
      hardCodedSessionId,
    );

  return useMutation<CreateOrderResponse, Error, CreateOrderInput>(
    async (input: CreateOrderInput) => {
      const jsonrpc = {
        jsonrpc: '2.0',
        ns: GLOBAL_PREFIX,
        method: 'orders/create',
        params: {
          ...input,
          licenseId,
          jwt,
          sessionId,
        },
        id: `${Date.now()}`,
      };

      const { data } = await axios.post(`${BASE_URL}/orders/create`, jsonrpc, {
        headers: { 'x-winamax-public-endpoint-version': 2 },
      });

      if (data.error) {
        throw new Error(
          data.error.data.message?.join(' | ') ?? data.error.message,
        );
      }

      return data.result;
    },
    {
      onSuccess: (response) => {
        if (response.status === CreateOrderResponseStatus.SUCCESS) emptyCart();
        if (response.status === CreateOrderResponseStatus.PARTIAL) {
          partiallyEmptyCart(response.errors);
          setOrderErrors(response.errors);
        }
        queryClient.refetchQueries(['orders/all']);
      },
    },
  );
}

export default useCreateOrder;
